import React, { useState, useEffect, useRef, Component } from 'react';
import axios from 'axios';
import './App.css';
import comtogetherLogo from './assets/logo_comtogether_RVB.jpg';
import backgroundImage from './assets/background-comtogether-scaled.jpg';

// Update this URL to point to your deployed backend
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://google-search-ads-generator.onrender.com' 
  : 'http://localhost:5001';

// Login Form as a Class Component
class LoginFormClass extends Component {
  constructor(props) {
    super(props);
    this.state = {
      username: '',
      password: '',
      isLoading: false
    };
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  }

  handleSubmit = async (e) => {
    e.preventDefault();
    const { onLogin, setLoginError } = this.props;
    
    // Prevent multiple submissions
    if (this.state.isLoading) return;
    
    this.setState({ isLoading: true });
    setLoginError(''); // Clear any previous errors
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        username: this.state.username,
        password: this.state.password
      }, {
        timeout: 10000, // 10 second timeout
        withCredentials: true
      });
      
      if (response.data.success) {
        onLogin(response.data, this.state.username);
      } else {
        setLoginError(response.data.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login Error:', err);
      if (err.code === 'ECONNABORTED') {
        setLoginError('Request timed out. Please try again in 1 minute.');
      } else if (!err.response) {
        setLoginError('Network error. Please check your internet connection.');
      } else if (err.response.status === 503) {
        setLoginError('Service temporarily unavailable. Please try again in a few minutes.');
      } else if (err.response.status === 401) {
        setLoginError('Invalid username or password.');
      } else {
        setLoginError('Error connecting to the server. Please try again.');
      }
    } finally {
      this.setState({ isLoading: false });
    }
  }

  render() {
    const { loginError } = this.props;
    const { isLoading } = this.state;
    return (
      <form onSubmit={this.handleSubmit} className="login-form">
        <div className="form-group">
          <label htmlFor="username">Username:</label>
          <input
            type="text"
            id="username"
            name="username"
            value={this.state.username}
            onChange={this.handleChange}
            required
            autoComplete="username"
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            name="password"
            value={this.state.password}
            onChange={this.handleChange}
            required
            autoComplete="current-password"
            disabled={isLoading}
          />
        </div>
        {loginError && <div className="error-message">{loginError}</div>}
        <button 
          type="submit" 
          className="login-button"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    );
  }
}

function App() {
  const [formData, setFormData] = useState({
    landing_page: '',
    keywords: '',
    style_angle: '',
    product_type: '',
    company_name: '',
    model_type: 'gpt-3.5-turbo', // Default to GPT-3.5
    language: 'English', // Default language
    use_templates: false // Whether to use templates instead of ChatGPT
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [selectedHeadlines, setSelectedHeadlines] = useState([]);
  const [selectedDescriptions, setSelectedDescriptions] = useState([]);
  const [showExportTable, setShowExportTable] = useState(false);
  const [headlinePositions, setHeadlinePositions] = useState([]);
  const [headlineCategories, setHeadlineCategories] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [userQuota, setUserQuota] = useState(null);
  const [userUsed, setUserUsed] = useState(null);
  const [currentUser, setCurrentUser] = useState('');
  const [isQuotaUpdating, setIsQuotaUpdating] = useState(false);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  
  // Add countdown timer effect
  useEffect(() => {
    let timer;
    if (isGenerating && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsGenerating(false);
    }
    return () => clearInterval(timer);
  }, [isGenerating, countdown]);

  const handleLoginSuccess = (data, username) => {
    setIsLoggedIn(true);
    setCurrentUser(username);
    setUserQuota(data.quota);
    setUserUsed(data.used);
  };

  // Add quota update animation
  useEffect(() => {
    if (isQuotaUpdating) {
      const timer = setTimeout(() => {
        setIsQuotaUpdating(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isQuotaUpdating]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if quota is exceeded before making the API call
    if (userUsed >= userQuota) {
      setError('Your quota has been exceeded. Please contact your Comtogether account manager for a top-up.');
      return;
    }

    setIsLoading(true);
    setError('');
    setResults(null);
    setSelectedHeadlines([]);
    setSelectedDescriptions([]);
    setHeadlinePositions([]);
    setHeadlineCategories([]);
    setShowExportTable(false);
    setIsGenerating(true);
    setCountdown(60);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/generate`, {
        ...formData,
        username: currentUser,
      });
      
      if (response.data.success) {
        console.log('API Response:', response.data);
        
        // Process the response data
        const responseData = response.data.data;
        console.log('Raw response data:', responseData);
        
        // Initialize arrays for translations
        const headlines = responseData.headlines || [];
        const descriptions = responseData.descriptions || [];
        const headlineTranslations = responseData.headline_translations || [];
        const descriptionTranslations = responseData.description_translations || [];
        
        console.log('Headline translations:', headlineTranslations);
        console.log('Description translations:', descriptionTranslations);
        
        // Process headlines to extract translations if they're embedded in the text
        if (headlines.length > 0 && headlines[0].includes('|')) {
          headlines.forEach((headline, index) => {
            if (headline.includes('|')) {
              const [original, translation] = headline.split('|').map(text => text.trim());
              headlines[index] = original;
              headlineTranslations[index] = translation;
            }
          });
        }
        
        // Process descriptions to extract translations if they're embedded in the text
        if (descriptions.length > 0 && descriptions[0].includes('|')) {
          descriptions.forEach((description, index) => {
            if (description.includes('|')) {
              const [original, translation] = description.split('|').map(text => text.trim());
              descriptions[index] = original;
              descriptionTranslations[index] = translation;
            }
          });
        }
        
        // Update the response data with processed translations
        responseData.headlines = headlines;
        responseData.descriptions = descriptions;
        responseData.headline_translations = headlineTranslations;
        responseData.description_translations = descriptionTranslations;
        
        setResults(responseData);
        
        // Initialize headline positions and categories
        if (responseData.headlines) {
          setHeadlinePositions(new Array(responseData.headlines.length).fill(false));
          setHeadlineCategories(responseData.headline_categories || []);
        }

        // Update quota with animation
        if (response.data.used !== undefined || response.data.quota !== undefined) {
          setIsQuotaUpdating(true);
          // Increment the used count immediately
          setUserUsed(prevUsed => (prevUsed !== null ? prevUsed + 1 : 1));
          setUserQuota(response.data.quota);

          // Fetch the latest quota after generation
          try {
            const quotaResponse = await axios.get(`${API_BASE_URL}/api/quota/${currentUser}`);
            if (quotaResponse.data.success) {
              setUserUsed(quotaResponse.data.used);
              setUserQuota(quotaResponse.data.quota);
            }
          } catch (quotaErr) {
            console.error('Failed to fetch updated quota:', quotaErr);
          }
        }
      } else {
        // Check if the error is quota-related
        if (response.data.error && response.data.error.toLowerCase().includes('quota')) {
          setError('Your quota has been exceeded. Please contact your Comtogether account manager for a top-up.');
          // Update the quota to reflect the exceeded state
          await fetchLatestQuota();
        } else {
          setError(response.data.error || 'Failed to generate ads');
        }
      }
    } catch (err) {
      console.error('API Error:', err);
      // Check if the error response contains quota-related message
      if (err.response?.data?.error?.toLowerCase().includes('quota')) {
        setError('Your quota has been exceeded. Please contact your Comtogether account manager for a top-up.');
        // Update the quota to reflect the exceeded state
        await fetchLatestQuota();
      } else {
        setError('Error connecting to the API. Please try again later.');
      }
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
      setCountdown(0);
    }
  };

  // Add function to fetch latest quota
  const fetchLatestQuota = async () => {
    if (!currentUser) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/quota/${currentUser}`);
      if (response.data.success) {
        setUserUsed(response.data.used);
        setUserQuota(response.data.quota);
      }
    } catch (err) {
      console.error('Failed to fetch quota:', err);
    }
  };

  // Add effect to periodically fetch quota
  useEffect(() => {
    if (isLoggedIn && currentUser) {
      // Fetch quota immediately when logged in
      fetchLatestQuota();

      // Set up interval to fetch quota every 30 seconds
      const intervalId = setInterval(fetchLatestQuota, 30000);

      // Clean up interval on unmount or when logged out
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, currentUser]);

  // Helper function to filter headlines by category
  const getHeadlinesByCategory = (category) => {
    if (!results?.headlines || !results.headline_categories) return [];
    return results.headlines.filter((_, index) => results.headline_categories[index] === category);
  };

  const handleHeadlineSelect = (index) => {
    setSelectedHeadlines(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleDescriptionSelect = (index) => {
    setSelectedDescriptions(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleExport = () => {
    if (selectedHeadlines.length === 0 && selectedDescriptions.length === 0) {
      setError('Please select at least one headline or description to export');
      return;
    }
    
    setShowExportTable(true);
  };

  const handleCopyToClipboard = () => {
    // Create arrays for headlines and descriptions with their positions
    const headlines = Array(15).fill('');
    const headlinePositions = Array(15).fill('');
    const descriptions = Array(4).fill('');
    const descriptionPositions = Array(4).fill('');
    const paths = Array(2).fill('');
    const finalUrl = formData.landing_page;

    // Fill in the selected headlines and their positions
    selectedHeadlines.forEach((index, i) => {
      if (i < 15) {
        headlines[i] = results.headlines[index] || '';
        // Safely handle undefined headline positions
        headlinePositions[i] = (results.headline_positions && results.headline_positions[index]) || '';
      }
    });

    // Fill in the selected descriptions and their positions
    selectedDescriptions.forEach((index, i) => {
      if (i < 4) {
        descriptions[i] = results.descriptions[index] || '';
        // Safely handle undefined description positions
        descriptionPositions[i] = (results.description_positions && results.description_positions[index]) || '';
      }
    });

    // Create the header row
    const headerRow = [
      ...Array(15).fill().map((_, i) => `Headline ${i + 1}`),
      ...Array(15).fill().map((_, i) => `Headline ${i + 1} position`),
      ...Array(4).fill().map((_, i) => `Description ${i + 1}`),
      ...Array(4).fill().map((_, i) => `Description ${i + 1} position`),
      'Path 1',
      'Path 2',
      'Final URL',
      'Final mobile URL',
      'Tracking template',
      'Final URL suffix',
      'Custom parameters',
      'Campaign Status',
      'Ad Group Status',
      'Status',
      'Approval Status',
      'Comment'
    ].join('\t');

    // Create the data row
    const dataRow = [
      ...headlines,
      ...headlinePositions,
      ...descriptions,
      ...descriptionPositions,
      ...paths,
      finalUrl,
      '', // Final mobile URL
      '', // Tracking template
      '', // Final URL suffix
      '', // Custom parameters
      '', // Campaign Status
      '', // Ad Group Status
      '', // Status
      '', // Approval Status
      ''  // Comment
    ].join('\t');

    // Combine header and data rows
    const exportData = `${headerRow}\n${dataRow}`;

    // Copy to clipboard
    navigator.clipboard.writeText(exportData)
      .then(() => {
        alert('Data copied to clipboard successfully!');
      })
      .catch(err => {
        console.error('Failed to copy data:', err);
        alert('Failed to copy data to clipboard. Please try again.');
      });
  };
  
  // Add logout handler
  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser('');
    setUserQuota(null);
    setUserUsed(null);
    setLoginError('');
    setResults(null);
    setSelectedHeadlines([]);
    setSelectedDescriptions([]);
  };

  // Update QuotaDisplay component to include user info and logout
  const UserInfoAndQuota = () => {
    if (!isLoggedIn || userQuota === null || userUsed === null) return null;

    const percentage = (userUsed / userQuota) * 100;
    const remaining = userQuota - userUsed;
    const isQuotaExceeded = userUsed >= userQuota;

    return (
      <>
        <div className="user-info">
          <span className="user-info-text">Welcome, {currentUser}!</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
        <div className={`quota-display ${isQuotaUpdating ? 'updating' : ''} ${isQuotaExceeded ? 'quota-exceeded' : ''}`}>
          <p className="quota-title">Generation Quota</p>
          <div className="quota-numbers">
            {userUsed} / {userQuota}
          </div>
          <div className="quota-progress">
            <div 
              className="quota-progress-bar" 
              style={{ 
                width: `${Math.min(percentage, 100)}%`,
                background: isQuotaExceeded ? 'linear-gradient(135deg, #FF6B6B 0%, #FF5252 100%)' : undefined
              }}
            />
          </div>
          {isQuotaExceeded ? (
            <p className="quota-error">
              Quota exceeded. Please contact your Comtogether account manager for a top-up.
            </p>
          ) : remaining <= 5 ? (
            <p className="quota-warning">Low quota remaining!</p>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <img src={comtogetherLogo} alt="Comtogether Logo" className="logo" />
        <h1>Google Search Ads Generator</h1>
        <p className="subtitle">Only available in regions where ChatGPT is not restricted. If the request has timed out, please wait 1 minute before trying again to allow the app to reboot </p>
      </header>

      {!isLoggedIn ? (
        <LoginFormClass 
          loginError={loginError}
          setLoginError={setLoginError}
          onLogin={handleLoginSuccess}
        />
      ) : (
        <main>
          <UserInfoAndQuota />
          <form onSubmit={handleSubmit} className="ad-form">
            <div className="form-group">
              <label htmlFor="landing_page">Landing Page URL:</label>
              <input
                type="url"
                id="landing_page"
                value={formData.landing_page}
                onChange={(e) => setFormData({...formData, landing_page: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="keywords">Keywords (one per line):</label>
              <textarea
                id="keywords"
                value={formData.keywords}
                onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="style_angle">Style Angle:</label>
              <input
                type="text"
                id="style_angle"
                value={formData.style_angle}
                onChange={(e) => setFormData({...formData, style_angle: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="product_type">Product Type/Industry:</label>
              <input
                type="text"
                id="product_type"
                value={formData.product_type}
                onChange={(e) => setFormData({...formData, product_type: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="language">Language:</label>
              <select
                id="language"
                value={formData.language}
                onChange={(e) => setFormData({...formData, language: e.target.value})}
              >
                <option value="English">English</option>
                <option value="German">German</option>
                <option value="French">French</option>
                <option value="Italian">Italian</option>
                <option value="Spanish">Spanish</option>
                <option value="Swiss-German">Swiss-German</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="company_name">Company Name:</label>
              <input
                type="text"
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="model_type">Model Type:</label>
              <select
                id="model_type"
                value={formData.model_type}
                onChange={(e) => setFormData({...formData, model_type: e.target.value})}
              >
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="gpt-4">GPT-4</option>
              </select>
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.use_templates}
                  onChange={(e) => setFormData({...formData, use_templates: e.target.checked})}
                />
                Use Templates (When ChatGPT is not available, this the prebuilt templates)
              </label>
            </div>
            
            <button
              type="submit"
              disabled={isLoading || userUsed >= userQuota}
            >
              Generate Ads
            </button>
            {userUsed >= userQuota && (
              <div style={{ color: 'red', marginTop: 8 }}>
                You have reached your quota limit.
              </div>
            )}
          </form>

          {isLoading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Generating your ad content...</p>
              <p className="countdown">Estimated time remaining: {countdown} seconds</p>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          
          {results && (
            <div className="ad-content">
              <h2>Generated Ad Content</h2>
              
              <div className="generation-info">
                <p className="model-info">
                  Generated using: {formData.use_templates ? 'Template System' : formData.model_type}
                </p>
              </div>
              
              <div className="content-section">
                <h3>Headlines</h3>
                <div className="headlines-grid">
                  {results.headlines.map((headline, index) => (
                    <div 
                      key={index} 
                      className={`headline-item ${selectedHeadlines.includes(index) ? 'selected' : ''}`}
                      onClick={() => handleHeadlineSelect(index)}
                      data-category={results.headline_categories && results.headline_categories[index] ? results.headline_categories[index] : 'Unknown'}
                    >
                      <div className="headline-content">
                        <span className="headline-text">{headline}</span>
                        {formData.language !== 'English' && results.headline_translations && results.headline_translations[index] && (
                          <span className="translation-text"> {results.headline_translations[index]}</span>
                        )}
                      </div>
                      <span className="headline-category">{results.headline_categories && results.headline_categories[index] ? results.headline_categories[index] : 'Unknown'}</span>
                      <span className="headline-position">
                        {headlinePositions[index] ? 'Pinned' : 'Not Pinned'}
                      </span>
                      <span className="character-count">
                        {headline.length}/30 characters
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="content-section">
                <h3>Descriptions</h3>
                <div className="descriptions-grid">
                  {results.descriptions.map((description, index) => (
                    <div 
                      key={index} 
                      className={`description-item ${selectedDescriptions.includes(index) ? 'selected' : ''}`}
                      onClick={() => handleDescriptionSelect(index)}
                    >
                      <div className="description-content">
                        <span className="description-text">{description}</span>
                        {formData.language !== 'English' && results.description_translations && results.description_translations[index] && (
                          <span className="translation-text">{results.description_translations[index]}</span>
                        )}
                      </div>
                      <span className="character-count">
                        {description.length}/90 characters
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="export-section">
                <button 
                  className="export-button"
                  onClick={handleExport}
                  disabled={selectedHeadlines.length === 0 && selectedDescriptions.length === 0}
                >
                  Export Selected
                </button>
                <button 
                  className="try-again-button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  Try Again
                </button>
              </div>

              {showExportTable && (
                <div className="export-table-container">
                  <h3>Export Preview</h3>
                  <div className="export-preview">
                    <table className="export-table">
                      <thead>
                        <tr>
                          <th>Format</th>
                          <th>Selected Content</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Headline 1 - 15</td>
                          <td>{selectedHeadlines.map((index, i) => (
                            <div key={`headline-${index}`}>
                              {i + 1}. {results.headlines[index]}
                            </div>
                          ))}</td>
                        </tr>
                        <tr>
                          <td>Headline Positions 1 - 15</td>
                          <td>{selectedHeadlines.map((_, i) => (
                            <div key={`headline-pos-${i}`}>
                              {i + 1}. {results.headline_positions?.[i] || ''}
                            </div>
                          ))}</td>
                        </tr>
                        <tr>
                          <td>Description 1 - 4</td>
                          <td>{selectedDescriptions.map((index, i) => (
                            <div key={`description-${index}`}>
                              {i + 1}. {results.descriptions[index]}
                            </div>
                          ))}</td>
                        </tr>
                        <tr>
                          <td>Description Positions 1 - 4</td>
                          <td>{selectedDescriptions.map((_, i) => (
                            <div key={`description-pos-${i}`}>
                              {i + 1}. {results.description_positions?.[i] || ''}
                            </div>
                          ))}</td>
                        </tr>
                        <tr>
                          <td>Paths 1 - 2</td>
                          <td>
                            <div>1. </div>
                            <div>2. </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Final URL</td>
                          <td>{formData.landing_page}</td>
                        </tr>
                      </tbody>
                    </table>

                    <button 
                      className="copy-button"
                      onClick={handleCopyToClipboard}
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      )}
    </div>
  );
}

export default App; 
